import { Button, DropDown, Form, Modal } from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { ServiceDiscountCreate, ServiceDiscountTable } from 'models/api/serviceDiscount';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ModalFooter, ModalHeader } from 'reactstrap';
import { useServiceDiscountWorkplaceState } from './ServiceDiscount';
import ServiceDiscountChangeModalBody from './ServiceDiscountChangeModalBody';

type ServiceDiscountChangeModalProps = {
    isOpen: boolean;
    toggle: Dispatch<SetStateAction<boolean>>;
    tariffPlans: DropDown[];
};

const ServiceDiscountChangeModal = ({
    isOpen = false,
    toggle,
    tariffPlans,
}: ServiceDiscountChangeModalProps) => {
    const { t } = useTranslation();
    const { hasRole } = useAuth();
    const { currentItem, changeItemMutation, addItemMutation } = useServiceDiscountWorkplaceState();
    const createRole = 'serviceDiscountCreate';
    const updateRole = 'serviceDiscountUpdate';
    const [intervalError, setIntervalError] = useState(false);
    const intervalErrorCode = 'BackOfficeSpecifiedIntervalOfAmountsOverlapsWithExistingOneMsg';

    const initialItem: ServiceDiscountCreate = {
        tariffPlanId: null,
        startAmount: 0,
        endAmount: 0,
        approvalFeeDiscount: null,
        loanServiceFeeDiscount: null,
        guaranteeFeePercent: null,
        extensionApprovalFeeDiscount: null,
    };

    const changeInternalError = (value: boolean) => {
        setIntervalError(value);
    };

    const closeModal = () => {
        toggle(false);
    };

    const defaultValues = useMemo<ServiceDiscountCreate>(
        () => currentItem ?? initialItem,
        [currentItem]
    );

    const saveChanges = async (serviceDiscount: ServiceDiscountTable) => {
        currentItem
            ? changeItemMutation.mutate(
                  {
                      ...serviceDiscount,
                      id: currentItem.id,
                  },
                  {
                      onSuccess: () => {
                          changeInternalError(false);
                          closeModal();
                      },
                      onError: (e) => {
                          const errorCode = (e as AxiosError<AxiosErrorResponse>)?.response?.data
                              ?.errorCode;
                          if (errorCode === intervalErrorCode) {
                              changeInternalError(true);
                          } else {
                              changeInternalError(false);
                          }
                      },
                  }
              )
            : addItemMutation.mutate(
                  {
                      ...serviceDiscount,
                  },
                  {
                      onSuccess: () => {
                          changeInternalError(false);
                          closeModal();
                      },
                      onError: (e) => {
                          const errorCode = (e as AxiosError<AxiosErrorResponse>)?.response?.data
                              ?.errorCode;
                          if (errorCode === intervalErrorCode) {
                              changeInternalError(true);
                          } else {
                              changeInternalError(false);
                          }
                      },
                  }
              );
    };

    const formHasRole = (addRole: string, editRole: string): boolean =>
        currentItem ? hasRole(editRole) : hasRole(addRole);

    const disabled = useMemo(() => !formHasRole(createRole, updateRole), []);

    return (
        <Modal
            size="lg"
            isOpen={isOpen}
            toggle={closeModal}
            scrollable
            className="service-discount__modal"
        >
            <Form<ServiceDiscountTable>
                defaultValues={defaultValues}
                clearAfterSubmit={false}
                onSubmit={(data) => {
                    saveChanges(data);
                }}
                className="d-contents"
            >
                <ModalHeader toggle={closeModal}>
                    {currentItem
                        ? `${currentItem.startAmount} - ${currentItem.endAmount}`
                        : t('BackOfficeNew')}
                </ModalHeader>

                <ServiceDiscountChangeModalBody
                    disable={disabled}
                    intervalError={intervalError}
                    onChangeIntervalError={changeInternalError}
                    tariffPlans={tariffPlans}
                />

                <ModalFooter>
                    <Button color="secondary" onClick={closeModal}>
                        <Trans i18nKey="BackOfficeCancel" />
                    </Button>
                    <Button color="success" type={'submit'} disabled={disabled}>
                        <Trans i18nKey="BackOfficeSave" />
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default ServiceDiscountChangeModal;
